import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';

const SuperAdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [settingsExpanded, setSettingsExpanded] = useState(
    location.pathname.startsWith('/admin/settings')
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    return location.pathname === href;
  };

  const isSettingsActive = () => {
    return location.pathname.startsWith('/admin/settings');
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Top Navbar */}
      <nav className="bg-gray-900 shadow-lg border-b border-gray-700 fixed top-0 left-0 right-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/admin" className="flex items-center space-x-2">
                <div className="h-8 w-8 bg-sky-600 rounded-lg flex items-center justify-center">
                  <svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <span className="text-xl font-bold text-white">Exlva DARe</span>
                  <span className="text-xs text-gray-400 block">Super Admin Console</span>
                </div>
              </Link>
            </div>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2">
                <div className="text-sm text-right">
                  <div className="font-medium text-white">{user?.name}</div>
                  <div className="text-xs text-sky-400">Super Admin</div>
                </div>
              </div>
              
              <div className="h-8 w-8 rounded-full bg-sky-600 flex items-center justify-center">
                <span className="text-white font-medium text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 transition-colors duration-200"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1H3zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main content area with sidebar */}
      <div className="flex pt-16">
        {/* Left Sidebar */}
        <aside className="w-64 bg-gray-800 min-h-[calc(100vh-4rem)] fixed left-0 top-16 overflow-y-auto">
          <nav className="mt-4 px-3">
            {/* User Dashboard */}
            <Link
              to="/admin"
              className={`flex items-center px-4 py-3 mb-1 rounded-lg transition-colors duration-200 ${
                isActive('/admin')
                  ? 'bg-sky-600 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
              </svg>
              <span className="font-medium">User Dashboard</span>
            </Link>

            {/* Settings Section */}
            <div className="mt-2">
              <button
                onClick={() => setSettingsExpanded(!settingsExpanded)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors duration-200 ${
                  isSettingsActive()
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">Settings</span>
                </div>
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${settingsExpanded ? 'rotate-180' : ''}`} 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Settings Submenu */}
              {settingsExpanded && (
                <div className="ml-4 mt-1 border-l-2 border-gray-600 pl-4 space-y-1">
                  {/* Integration Settings Header */}
                  <div className="px-4 py-2 text-gray-500 text-xs uppercase tracking-wider font-semibold">
                    Integration Settings
                  </div>
                  
                  <Link
                    to="/admin/settings/email-service"
                    className={`flex items-center px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive('/admin/settings/email-service')
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <span className="text-sm">Email Service</span>
                  </Link>

                  <Link
                    to="/admin/settings/email-templates"
                    className={`flex items-center px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive('/admin/settings/email-templates')
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">Email Templates</span>
                  </Link>

                  {/* Account Settings Header */}
                  <div className="px-4 py-2 mt-3 text-gray-500 text-xs uppercase tracking-wider font-semibold">
                    Account Settings
                  </div>

                  <Link
                    to="/admin/settings/change-password"
                    className={`flex items-center px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive('/admin/settings/change-password')
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">Change Password</span>
                  </Link>

                  {/* Application Settings Header */}
                  <div className="px-4 py-2 mt-3 text-gray-500 text-xs uppercase tracking-wider font-semibold">
                    Application Settings
                  </div>

                  <Link
                    to="/admin/settings/footer"
                    className={`flex items-center px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive('/admin/settings/footer')
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="text-sm">Footer Settings</span>
                  </Link>

                  <Link
                    to="/admin/settings/purposes"
                    className={`flex items-center px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive('/admin/settings/purposes')
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    <span className="text-sm">AI Insight Categories</span>
                  </Link>

                  <Link
                    to="/admin/settings/theme"
                    className={`flex items-center px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive('/admin/settings/theme')
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                    <span className="text-sm">Theme Settings</span>
                  </Link>

                  {/* Integration Settings Header */}
                  <div className="px-4 py-2 mt-3 text-gray-500 text-xs uppercase tracking-wider font-semibold">
                    Integration Settings
                  </div>

                  <Link
                    to="/admin/settings/integrations/s3"
                    className={`flex items-center px-4 py-2.5 rounded-lg transition-colors duration-200 ${
                      isActive('/admin/settings/integrations/s3')
                        ? 'bg-sky-600 text-white'
                        : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                    <span className="text-sm">AWS S3 Storage</span>
                  </Link>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="my-4 border-t border-gray-700"></div>

            {/* Help/Info Section */}
            <div className="px-4 py-3">
              <div className="flex items-center text-gray-500 text-xs">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span>Super Admin Console</span>
              </div>
              <p className="text-gray-600 text-xs mt-1 ml-6">
                Manage users and system settings
              </p>
            </div>

          </nav>
        </aside>

        {/* Main Content */}
        {/* min-w-0 lets this flex child shrink below its content's natural
            width, so wide tables/grids scroll inside their own box instead of
            pushing the whole page wide (the 256px horizontal-scrollbar bug). */}
        <main className="flex-1 min-w-0 ml-64 p-4 sm:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default SuperAdminLayout;
